import React, { useEffect, useState } from "react";
import { Loader2, Mail, Paperclip, ExternalLink, Inbox } from "lucide-react";
import { searchEmailsByAddress } from "../lib/api";
import { fmtRelDate } from "./email/utils";

/**
 * Compact panel that lists recent emails to/from a customer address
 * across every connected mailbox. Rendered inside EditProjectDialog —
 * gives contractors instant context on prior conversation without
 * jumping to the Email page (but each row deep-links there if they
 * want the full thread).
 *
 * Empty/error states are intentionally quiet — many projects won't
 * have a linked email and that's fine.
 */
export default function CustomerEmailsPanel({ address, max = 5 }) {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const addr = (address || "").trim().toLowerCase();
        if (!addr) {
            setMessages([]);
            setError(null);
            return;
        }
        let dead = false;
        setLoading(true);
        setError(null);
        (async () => {
            try {
                const r = await searchEmailsByAddress(addr, max);
                if (dead) return;
                setMessages(r?.messages || []);
            } catch (err) {
                if (dead) return;
                setError(err?.response?.data?.detail || "Couldn't fetch emails");
                setMessages([]);
            } finally {
                if (!dead) setLoading(false);
            }
        })();
        return () => { dead = true; };
    }, [address, max]);

    if (!address) return null;

    return (
        <div
            className="pt-2 border-t border-[#2A3655]"
            data-testid="customer-emails-panel"
        >
            <div className="flex items-center justify-between mb-2">
                <div className="text-slate-300 text-xs uppercase tracking-wider flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5 text-[#00A3FF]" />
                    Recent emails
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    {address}
                </span>
            </div>
            <div className="rounded-sm border border-[#2A3655] bg-[#0A1128]/60 max-h-48 overflow-y-auto">
                {loading ? (
                    <div className="flex items-center justify-center py-4 text-slate-500 text-xs">
                        <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> Searching mailboxes…
                    </div>
                ) : error ? (
                    <div className="px-3 py-3 text-xs text-amber-300/90 italic">{error}</div>
                ) : messages.length === 0 ? (
                    <div className="px-3 py-4 text-center text-xs italic text-slate-500">
                        <Inbox className="w-4 h-4 mx-auto mb-1.5 text-slate-600" />
                        No emails found from <span className="text-slate-400">{address}</span> yet.
                    </div>
                ) : (
                    messages.map((m) => (
                        <a
                            key={`${m._account_id}_${m._folder}_${m.uid}`}
                            href="/email"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`customer-email-row-${m.uid}`}
                            className="block px-3 py-2 border-b border-[#2A3655]/40 last:border-b-0 hover:bg-[#1C253C]/60 transition-colors"
                            title={`Open in Email tab — ${m._account_email}`}
                        >
                            <div className="flex items-baseline gap-2">
                                <span className={`text-xs truncate flex-1 min-w-0 ${m.seen ? "text-slate-300" : "text-white font-semibold"}`}>
                                    {m.from_name || m.from_addr || "(unknown)"}
                                </span>
                                <span className="text-[10px] text-slate-500 shrink-0">{fmtRelDate(m.date)}</span>
                                <ExternalLink className="w-3 h-3 text-slate-500 shrink-0" />
                            </div>
                            <div className={`text-[11px] truncate ${m.seen ? "text-slate-400" : "text-slate-200"}`}>
                                {m.subject || "(no subject)"}
                            </div>
                            <div className="text-[10px] text-slate-500 truncate mt-0.5 flex items-center gap-1">
                                {m.has_attachments && <Paperclip className="w-2.5 h-2.5" />}
                                <span className="text-[#00A3FF]/70 font-bold">{m._folder}</span>
                                <span>·</span>
                                <span className="truncate">{m._account_email}</span>
                            </div>
                        </a>
                    ))
                )}
            </div>
        </div>
    );
}
